import { useContext, useMemo } from 'react'
import * as React from 'react'
import type { PolymorphicComponentProps } from 'styled-components'
import styled, { css } from 'styled-components'
import clsx from 'clsx'
import { breakpointSizes, mediaQueryUp, mediaQueryDown, mediaQueryOnly } from 'components/utils/breakpoint'
import { objectKeys } from 'lib/object/objectUtils'
import type { StyledTarget } from 'styled-components/dist/types'
import { AppMountedContext } from 'contexts/AppMountedContext'
import Breakpoint from 'components/Common/Breakpoint'

const sizes = objectKeys(breakpointSizes)
const precompiledCss = sizes.map(size => {
  if (size === 'mobile') {
    return css`
      &.hidedown-${size} {
        ${mediaQueryDown[size]} { display: none; }
      }

      &.hideonly-${size} {
        ${mediaQueryOnly[size]} { display: none; }
      }
    `
  } else if (size === 'extraLargeDesktop') {
    return css`
      &.hideup-${size} {
        ${mediaQueryUp[size]} { display: none; }
      }

      &.hideonly-${size} {
        ${mediaQueryOnly[size]} { display: none; }
      }
    `
  } else {
    return css`
      &.hidedown-${size} {
        ${mediaQueryDown[size]} { display: none; }
      }

      &.hideup-${size} {
        ${mediaQueryUp[size]} { display: none; }
      }

      &.hideonly-${size} {
        ${mediaQueryOnly[size]} { display: none; }
      }
    `
  }
})

const BreakpointWrapper = styled.div`
  &:not(.show-when-empty) {
    &:empty:not(:is(br, hr, input, textarea)) {
      display: none;
    }
  }
`

const UpDownBreakpoint = styled(BreakpointWrapper)`
  ${precompiledCss}
`

interface Props extends React.ComponentProps<typeof UpDownBreakpoint> {
  min?: App.ScreenSize
  max?: App.ScreenSize
  only?: App.ScreenSize
  not?: App.ScreenSize
  ref?: React.RefObject<HTMLDivElement | null>
  showWhenEmpty?: boolean
  /**
   * If true, the component will switch from CSS-based hiding of elements to JS-based conditional rendering
   * after the app has mounted. At some future point this will become the default functionality.
   */
  dynamic?: boolean
}

/**
 * Shows/hides its children at the specified screen sizes using CSS. This is strongly preferred to conditionally
 * rendering elements based on screen size using `useScreenSizeUp` and similar, because when the app is server-
 * rendered, the server doesn't know the user's screen size. Therefore it can't correctly use JS to evaluate what
 * to render.
 *
 * `CSSBreakpoint` by contrast will always render the contents, and applies CSS to hide it at certain screen sizes.
 * The browser will then show the correct thing for the screen size before the JS has loaded.
 *
 * After the app mounts, the component can then safely switch to JS-based conditional rendering so React doesn't
 * need to render stuff that won't be shown. This is currently opt-in behaviour via the `dynamic` prop.
 */
function CSSBreakpoint<AsTarget extends StyledTarget<'web'> = 'div'>(props: PolymorphicComponentProps<'web', Props, AsTarget, void>) {
  const { min, max, only, not, className, children, showWhenEmpty, dynamic, ...rest } = props

  const appHasMounted = useContext(AppMountedContext)

  const onlyOrMin = only ?? min
  const onlyOrMax = only ?? max

  const hideDownSize = useMemo(() => {
    if (onlyOrMin) {
      const minIndex = sizes.indexOf(onlyOrMin)
      return sizes[minIndex - 1]
    }
  }, [onlyOrMin])

  const hideUpSize = useMemo(() => {
    if (onlyOrMax) {
      const maxIndex = sizes.indexOf(onlyOrMax)
      return sizes[maxIndex + 1]
    }
  }, [onlyOrMax])

  if (dynamic && appHasMounted) {
    // When the app has mounted, show/hide element with JS
    return <Breakpoint min={min} max={max} only={only} not={not}>
      <BreakpointWrapper {...rest} className={clsx(className, { 'show-when-empty': showWhenEmpty })}>
        {children}
      </BreakpointWrapper>
    </Breakpoint>
  } else {
    // Before the app mounts, show/hide element with CSS to match the server render
    const classes = clsx(className, {
      [`hidedown-${hideDownSize}`]: hideDownSize,
      [`hideup-${hideUpSize}`]: hideUpSize,
      [`hideonly-${not}`]: not,
      'show-when-empty': showWhenEmpty,
    })

    return <UpDownBreakpoint {...rest} className={classes}>
      {children}
    </UpDownBreakpoint>
  }
}

export default CSSBreakpoint
